import React, { useEffect, useMemo, useState } from 'react'
import {
  Dialog,
  DialogContent,
  DialogActions,
  Box,
  Typography,
  IconButton,
  TextField,
  Select,
  MenuItem,
  FormControl,
  Button,
  Alert,
} from '@mui/material'
import {
  Close as CloseIcon,
  Person as PersonIcon,
  Description as DescriptionIcon,
  AttachMoney as AttachMoneyIcon,
} from '@mui/icons-material'
import { format } from 'date-fns'
import { BatchInvoice, formatCurrency } from '../mockData'

export interface CreatePaymentBatchPayload {
  name: string
  month: string
  description: string
}

interface CreatePaymentBatchModalProps {
  open: boolean
  onClose: () => void
  selectedInvoices: BatchInvoice[]
  onCreate: (payload: CreatePaymentBatchPayload) => void
  // Controls the wording (invoices vs claims). Defaults to invoice.
  batchType?: 'invoice' | 'claim'
  error?: string | null
}

// The current month, e.g. "May 2026".
const currentMonthLabel = () => format(new Date(), 'MMMM yyyy')

// Twelve months of the current year, so the dropdown always covers "now".
const buildMonthOptions = () => {
  const year = new Date().getFullYear()
  return Array.from({ length: 12 }, (_, i) =>
    format(new Date(year, i, 1), 'MMMM yyyy')
  )
}

const CreatePaymentBatchModal: React.FC<CreatePaymentBatchModalProps> = ({
  open,
  onClose,
  selectedInvoices,
  onCreate,
  batchType = 'invoice',
  error = null,
}) => {
  const monthOptions = useMemo(buildMonthOptions, [])
  const [name, setName] = useState(
    () => `Provider Payroll – ${currentMonthLabel()}`
  )
  const [month, setMonth] = useState(currentMonthLabel)
  const [description, setDescription] = useState('')

  // Reset Batch Month (and matching default name) to the current month each
  // time the modal is opened.
  useEffect(() => {
    if (open) {
      const current = currentMonthLabel()
      setMonth(current)
      setName(`Provider Payroll – ${current}`)
      setDescription('')
    }
  }, [open])

  const itemLabel = batchType === 'claim' ? 'claims' : 'invoices'
  const itemLabelCap = batchType === 'claim' ? 'Claims' : 'Invoices'

  const summary = useMemo(() => {
    const providerIds = new Set(selectedInvoices.map(inv => inv.provider._id))
    const total = selectedInvoices.reduce(
      (sum, inv) => sum + (inv.totalAmount || 0),
      0
    )
    const providerPayout = selectedInvoices.reduce(
      (sum, inv) => sum + (inv.providerPayout || 0),
      0
    )
    return {
      providers: providerIds.size,
      invoices: selectedInvoices.length,
      total,
      providerPayout,
    }
  }, [selectedInvoices])

  const handleCreate = () => {
    onCreate({ name, month, description })
  }

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', p: 3, pb: 1 }}>
        <Box>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1e293b' }}>
            Create Provider Payment Batch
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
            Group selected provider {itemLabel} into a batch for payout
            processing.
          </Typography>
        </Box>
        <IconButton onClick={onClose} size="small" sx={{ alignSelf: 'flex-start' }}>
          <CloseIcon />
        </IconButton>
      </Box>

      <DialogContent sx={{ pt: 2 }}>
        {error && (
          <Alert severity="error" sx={{ mb: 2, borderRadius: 2 }}>
            {error}
          </Alert>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
          Batch Name
        </Typography>
        <TextField
          fullWidth
          size="small"
          value={name}
          onChange={e => setName(e.target.value)}
          sx={{ mb: 2.5, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        <Box sx={{ mb: 2.5 }}>
          <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
            Batch Month
          </Typography>
          <FormControl fullWidth size="small">
            <Select
              value={month}
              onChange={e => setMonth(e.target.value)}
              sx={{ borderRadius: 2 }}
            >
              {monthOptions.map(opt => (
                <MenuItem key={opt} value={opt}>
                  {opt}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, mb: 0.75 }}>
          Description{' '}
          <Typography component="span" variant="caption" color="text.secondary">
            (optional)
          </Typography>
        </Typography>
        <TextField
          fullWidth
          multiline
          minRows={3}
          placeholder="Notes for this batch..."
          value={description}
          onChange={e => setDescription(e.target.value)}
          sx={{ mb: 3, '& .MuiOutlinedInput-root': { borderRadius: 2 } }}
        />

        <Box
          sx={{
            border: '1px solid #e2e8f0',
            borderRadius: 2,
            p: 2,
            bgcolor: '#f8fafc',
          }}
        >
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: '0.06em',
              color: '#64748b',
            }}
          >
            BATCH SUMMARY
          </Typography>
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: 'repeat(2, 1fr)',
              gap: 1.5,
              mt: 1.5,
            }}
          >
            <SummaryTile
              icon={<PersonIcon fontSize="small" />}
              label="Providers"
              value={String(summary.providers)}
            />
            <SummaryTile
              icon={<DescriptionIcon fontSize="small" />}
              label={itemLabelCap}
              value={String(summary.invoices)}
            />
            <SummaryTile
              icon={<AttachMoneyIcon fontSize="small" />}
              label="Total Amount"
              value={formatCurrency(summary.total)}
            />
            <SummaryTile
              icon={<AttachMoneyIcon fontSize="small" />}
              label="Provider Payout"
              value={formatCurrency(summary.providerPayout)}
            />
          </Box>
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 3, pt: 1 }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{ textTransform: 'none', borderRadius: 2 }}
        >
          Cancel
        </Button>
        <Button
          onClick={handleCreate}
          variant="contained"
          disabled={!name.trim() || selectedInvoices.length === 0}
          sx={{
            textTransform: 'none',
            borderRadius: 2,
            bgcolor: '#7A6FA1',
            '&:hover': { bgcolor: '#655a8a' },
          }}
        >
          Create Batch
        </Button>
      </DialogActions>
    </Dialog>
  )
}

const SummaryTile: React.FC<{
  icon: React.ReactNode
  label: string
  value: string
}> = ({ icon, label, value }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid #e2e8f0',
      borderRadius: 1.5,
      p: 1.25,
    }}
  >
    <Box
      sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: '#64748b' }}
    >
      {icon}
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
    </Box>
    <Typography variant="subtitle2" sx={{ fontWeight: 700, mt: 0.5 }}>
      {value}
    </Typography>
  </Box>
)

export default CreatePaymentBatchModal
